<script setup lang="ts">
import { toRef, reactive, ref, watch, computed } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useUserSettingsStore } from '@/stores/userSettings'
import { useAuthStore } from '@/stores/auth.ts'
import { useUserStore } from '@/stores/userStore'
import { SWITCH_PROJECT, USER_SETTING } from '@/services/apiModules'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Settings } from '@lucide/vue'
import { useApiMutation } from '@/composables/useApiMutation'
import { getInitials } from '@/utils/getInitials'

defineProps<{
  isTopRight?: boolean
}>()

const currentYear = new Date().getFullYear()
const auth = useAuthStore()
const userStore = useUserStore()
const userSettingsStore = useUserSettingsStore()
const { t } = useI18n()
const router = useRouter()

const theme = ref(userSettingsStore.theme)
const isAvatarMissing = ref(false)
const popoverOpen = ref(false)

const isDarkMode = computed({
  get: () => theme.value === 'dark',
  set: (val: boolean) => {
    theme.value = val ? 'dark' : 'light'
    toggleDarkMode()
  },
})

const vorname = toRef(userStore, 'vorname')
const nachname = toRef(userStore, 'nachname')
const fullName = computed(() => {
  const v = (vorname.value ?? '').trim()
  const n = (nachname.value ?? '').trim()
  return `${v} ${n}`.trim()
})

const user = reactive({
  name: fullName,
  email: toRef(userStore, 'email'),
  company: toRef(userStore, 'firma'),
  avatar: toRef(userStore, 'avatar'),
})

function toggleDarkMode() {
  userSettingsStore.updateSettings({ theme: theme.value })
  updateSettings()
}

function logout() {
  auth.logout()
}

function onAvatarError() {
  isAvatarMissing.value = true
}

function goToSwitchAccount() {
  closePopover()
  switchAccount()
}

function closePopover() {
  popoverOpen.value = false
}

const { mutateAsync, isPending } = useApiMutation({
  modus: USER_SETTING,
})

const { mutateAsync: mutateSwitchAsync, isPending: isSwitchPending } = useApiMutation({
  modus: SWITCH_PROJECT,
})
async function updateSettings() {
  try {
    await mutateAsync({
      payload: {
        settings: {
          language: userSettingsStore.language,
          theme: userSettingsStore.theme,
        },
      },
      method: 'PUT',
    })
  } catch (error) {
    console.error('Error updating settings:', error)
  }
}

async function switchAccount() {
  try {
    await mutateSwitchAsync({
      payload: {},
    })
    userStore.registerUser({ firma: null })
    router.push({ name: 'login' })
  } catch (error) {
    console.error('Switch account failed:', error)
  }
}

watch(
  () => userSettingsStore.theme,
  (newValue) => {
    theme.value = newValue
  },
)

watch(
  () => userStore.avatar,
  (newValue) => {
    isAvatarMissing.value = !newValue
  },
)
</script>

<template>
  <Popover v-model:open="popoverOpen">
    <PopoverTrigger>
      <div class="h-10 w-10">
        <img
          v-if="!isAvatarMissing"
          :src="user.avatar ?? ''"
          alt="User"
          @error="onAvatarError"
          class="rounded-full"
        />
        <div
          v-else
          class="bg-tertiary text-primary flex h-10 w-10 cursor-pointer items-center justify-center rounded-full text-xl font-bold dark:text-white"
        >
          {{ getInitials(user.name) }}
        </div>
      </div>
    </PopoverTrigger>

    <PopoverContent
      :side="isTopRight ? 'bottom' : 'right'"
      align="end"
      :alignOffset="isTopRight ? 0 : -25"
      :sideOffset="20"
      :avoid-collisions="true"
      :disable-outside-pointer-events="isTopRight ? true : false"
      class="w-90 rounded-xl p-0 shadow-md"
    >
      <Card class="gap-2 overflow-hidden rounded-xl border py-2 dark:border-gray-600">
        <!-- User Info -->
        <CardHeader class="flex items-center gap-4 px-4 pt-4">
          <div class="h-15 w-15">
            <img
              v-if="!isAvatarMissing"
              :src="user.avatar ?? ''"
              alt="Avatar"
              @error="onAvatarError"
              class="border-foreground rounded-full border"
            />
            <div
              v-else
              class="bg-tertiary text-primary flex h-15 w-15 items-center justify-center rounded-full border text-3xl font-bold dark:text-white"
            >
              {{ getInitials(user.name) }}
            </div>
          </div>

          <div class="flex flex-col gap-1">
            <p class="text-xs text-gray-500">{{ user.email }}</p>
            <p class="text-xl leading-tight font-bold">{{ user.name }}</p>
            <p v-if="user.company" class="text-sm">{{ user.company }}</p>
          </div>
        </CardHeader>

        <!-- Buttons -->
        <CardContent class="justify-left flex gap-5 border-b border-gray-300 px-4 py-2 pb-4">
          <RouterLink :to="{ name: 'user-settings' }">
            <Button
              variant="outline"
              @click="closePopover"
              class="cursor-pointer border text-sm dark:border-gray-400 dark:hover:brightness-110"
            >
              <Settings class="text-primary dark:text-tertiary h-4 w-4" />
              <span class="text-primary dark:text-tertiary"> {{ t('layout.settings') }} </span>
            </Button>
          </RouterLink>

          <div class="flex items-center gap-1">
            <Icon icon="fa:sun-bright-regular-full" class="size-5 text-gray-500" />

            <Switch
              v-model="isDarkMode"
              :disabled="isPending"
              :aria-label="$t('userSettings.theme')"
              class="cursor-pointer"
            />
            <Icon icon="fa:moon-regular-full" class="size-5 text-gray-500" />
          </div>
        </CardContent>

        <!-- Switch & Logout -->
        <CardContent
          class="text-primary text-md flex flex-col gap-2 border-b border-gray-300 px-5 pb-3 font-bold"
        >
          <RouterLink
            v-if="userStore.isMultiAccountUser"
            @click="goToSwitchAccount"
            :to="{ name: 'login' }"
            :class="isSwitchPending ? 'disabled pointer-events-none text-gray-400' : ''"
            class="w-fit cursor-pointer text-sm hover:brightness-75 dark:hover:brightness-110"
          >
            {{ t('layout.switchProject') }}
          </RouterLink>
          <a
            variant="link"
            @click="logout"
            class="dark:text-tertiary w-fit cursor-pointer text-sm hover:brightness-75 dark:hover:brightness-110"
          >
            {{ t('layout.logout') }}</a
          >
        </CardContent>

        <!-- Footer -->
        <CardFooter
          class="flex flex-col items-start px-5 py-0 text-left text-[10px] text-gray-400 dark:text-gray-500"
        >
          <p>
            <a href="https://impressum.twlegaltech.de/" target="_blank">
              {{ t('layout.privacyPolicy') }}
            </a>
          </p>
          <p>© {{ currentYear }} {{ t('layout.companyName') }}</p>
        </CardFooter>
      </Card>
    </PopoverContent>
  </Popover>
</template>
